// b-share (COMMERCE_BUILD.md §13 POS): a share link `https://<shop>/?code=CODE`
// (any page of the site) keeps the code in a short first-party cookie, so it
// survives browsing to checkout, where it is applied through the normal code
// route (same checks, Cap and the one generic failure). The `?code=` is taken
// off the address straight away (history.replaceState), so a shared or
// bookmarked page isn't a different page - and nothing is rendered from it
// on the server: the SSR and the page cache never see the code (S5's
// "cached page" concern). The note is drawn in the browser only.
//
// Pure helpers here (tests run them); the cookie is read and written with
// `document.cookie` by the small wrappers at the bottom.

export const CODE_COOKIE = "dsk_code";
export const CODE_DAYS = 7;
const CODE_RE = /^[A-Z0-9-]{1,40}$/;

// A code as a shopper might type it -> the shop's form (upper case), or "".
export function cleanCode(v) {
    const c = String(v ?? "").trim().toUpperCase();
    return CODE_RE.test(c) ? c : "";
}

// The ?code= of an address's query string ("?a=1&code=spring5" -> "SPRING5").
export function codeFromSearch(search) {
    try {
        return cleanCode(new URLSearchParams(String(search || "")).get("code"));
    } catch (e) {
        return "";
    }
}

// The same address without its code (path, other query, hash kept).
export function withoutCode(href) {
    try {
        const u = new URL(String(href));
        if (!u.searchParams.has("code")) return null;
        u.searchParams.delete("code");
        const q = u.searchParams.toString();
        return `${u.pathname}${q ? `?${q}` : ""}${u.hash}`;
    } catch (e) {
        return null;
    }
}

// The kept code from a cookie header ("a=1; dsk_code=SPRING5").
export function codeFromCookies(cookieText) {
    for (const part of String(cookieText || "").split(";")) {
        const [k, ...v] = part.trim().split("=");
        if (k === CODE_COOKIE) {
            try {
                return cleanCode(decodeURIComponent(v.join("=")));
            } catch (e) {
                return "";
            }
        }
    }
    return "";
}

export function cookieFor(code, {secure = true, days = CODE_DAYS} = {}) {
    const c = cleanCode(code);
    if (!c) return `${CODE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure ? "; Secure" : ""}`;
    return `${CODE_COOKIE}=${encodeURIComponent(c)}; Path=/; Max-Age=${days * 86400}; SameSite=Lax${secure ? "; Secure" : ""}`;
}

export const noteText = (code) => `Code ${code} will be applied at checkout.`;

// What checkout does with a kept code: apply it once when the checkout is
// live and has no code; whatever happens, the code is then forgotten (a
// failure shows the generic message once; a removed code isn't re-applied).
export function shouldApply({code, view, tried}) {
    return !!cleanCode(code) && !tried && view?.status === "pending_payment" && !view?.code;
}

// Checkout's one try (CheckoutView runs it whenever the view changes, and at
// once when it mounts): read the kept code; on a live checkout without a
// code, forget it FIRST (so it's never tried twice, whatever happens) and
// apply it through `apply` (the normal code route). -> {code, result} | null
export async function applyPendingCode({read, forget, apply, view, state}) {
    const code = read();
    if (!shouldApply({code, view, tried: state.tried})) return null;
    state.tried = true;
    forget();
    return {code, result: await apply(code)};
}

// --- the browser ------------------------------------------------------------------

const inBrowser = () => typeof document !== "undefined" && typeof window !== "undefined";
const secureHere = () => inBrowser() && window.location.protocol === "https:";

export function readPendingCode() {
    return inBrowser() ? codeFromCookies(document.cookie) : "";
}
export function forgetPendingCode() {
    if (inBrowser()) document.cookie = cookieFor("", {secure: secureHere()});
}
// On every page load and route change: keep a ?code= and take it off the address.
export function capturePendingCode() {
    if (!inBrowser()) return "";
    const code = codeFromSearch(window.location.search);
    const clean = withoutCode(window.location.href);
    if (code) document.cookie = cookieFor(code, {secure: secureHere()});
    if (clean !== null) {
        try { window.history.replaceState(window.history.state, "", clean); } catch (e) { /* old browsers: the code stays on the address */ }
    }
    return code;
}
